import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { contractorAuthAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Building2, Loader2, HardHat, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const ContractorLoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const projectIdFromUrl = searchParams.get('project') || '';
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [projectId, setProjectId] = useState(projectIdFromUrl);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!email || !password || !projectId) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      const response = await contractorAuthAPI.login({
        email,
        password,
        project_id: projectId
      });
      
      // Store contractor token and info
      localStorage.setItem('contractor_token', response.data.access_token);
      localStorage.setItem('contractor_info', JSON.stringify(response.data.contractor));
      localStorage.setItem('contractor_project', JSON.stringify(response.data.project));
      
      toast.success(`Welcome, ${response.data.contractor.name}!`);
      navigate('/contractor/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid-bg flex items-center justify-center p-4">
      <Card className="w-full max-w-md border-border bg-card rounded-sm">
        <CardHeader className="text-center">
          <div className="w-16 h-16 rounded-sm bg-amber-500/10 border border-amber-500/30 flex items-center justify-center mx-auto mb-4">
            <HardHat className="w-8 h-8 text-amber-500" />
          </div>
          <CardTitle className="text-2xl font-bold font-heading">Contractor Login</CardTitle>
          <CardDescription>Sign in to upload documents for your project</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="contractor@example.com"
                className="bg-input/50 border-border focus:border-primary rounded-sm h-11"
                data-testid="contractor-email-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Password</Label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="bg-input/50 border-border focus:border-primary rounded-sm h-11 pr-10"
                  data-testid="contractor-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Project ID</Label>
              <Input
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
                placeholder="Project ID provided by owner"
                className="bg-input/50 border-border focus:border-primary rounded-sm h-11 font-mono text-sm"
                data-testid="contractor-project-input"
              />
              <p className="text-xs text-muted-foreground">
                Ask the property owner for your project ID
              </p>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-amber-500 text-black hover:bg-amber-400 rounded-sm h-11 font-semibold"
              data-testid="contractor-login-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          <div className="mt-6 pt-6 border-t border-border text-center">
            <p className="text-sm text-muted-foreground mb-2">Have a portal link instead?</p>
            <Button
              variant="outline"
              onClick={() => navigate('/contractor/upload')}
              className="rounded-sm"
            >
              Use Portal Link
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ContractorLoginPage;
